import Avatar from 'src/components/ui/avatar';
import { Dropdown } from 'src/components/ui/dropdown';
import { Icon } from 'src/components/ui/icon';
import Tooltip from 'src/components/ui/tooltip';
import { Typography } from 'src/components/ui/typography';
import { ONBOARDING_STEPS_TARGET } from 'src/components/UserOnboard/constants';
import HelpAndSupportModal from 'src/content/HelpAndSupport/HelpAndSupportModal';
import { SuccessDialog } from 'src/content/HelpAndSupport/SuccessModal';
import { useProfile } from 'src/layouts/SidebarV2/hooks';
import { sidebarIcons } from 'src/layouts/SidebarV2/icons';
import { NavItem } from 'src/layouts/SidebarV2/primitives';
import type { RootState } from 'src/redux/store';
import { useSelector } from 'src/redux/store';

export interface ProfileProps {
  initials: string;
  collapsed?: boolean;
  onToggle: () => void;
}

const Profile = ({ initials, collapsed = false, onToggle }: ProfileProps) => {
  const {
    name,
    accountTypeLabel,
    isSubmitting,
    isMenuOpen,
    setIsMenuOpen,
    menuItems,
    isSupportOpen,
    closeSupport,
    isSuccessOpen,
    setIsSuccessOpen,
    closeSuccess,
    supportTab,
    setSupportTab,
    setIsSubmitting
  } = useProfile();

  const { orgLogo } = useSelector((state: RootState) => state.data);
  const logo = orgLogo.collapsedNavbarLogo ?? null;
  const { isOnboarding } = useSelector((state: RootState) => state.onboarding);

  const collapseToggle = (
    <NavItem
      name={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      icon={sidebarIcons.layoutLeft}
      iconOnly
      onClick={onToggle}
    />
  );

  const supportModals = (
    <>
      {isSupportOpen ? (
        <HelpAndSupportModal
          isOpen={isSupportOpen}
          onClose={closeSupport}
          isSuccessDialogOpen={isSuccessOpen}
          setIsSuccessDialogOpen={setIsSuccessOpen}
          selectedTab={supportTab}
          setSelectedTab={setSupportTab}
          setIsSubmitting={setIsSubmitting}
        />
      ) : null}
      {isSuccessOpen ? (
        <SuccessDialog
          open={isSuccessOpen}
          onClose={closeSuccess}
          tab={supportTab}
        />
      ) : null}
    </>
  );

  const avatar = logo ? (
    <Avatar
      type="image"
      src={logo}
      size="sm"
      variant="round"
      className="tw:shrink-0"
    />
  ) : (
    <Avatar type="text" text={initials} size="sm" className="tw:shrink-0" />
  );

  if (collapsed) {
    return (
      <div className="tw:flex tw:flex-col tw:items-center tw:gap-m tw:w-full">
        <Dropdown
          trigger={
            <button
              id={ONBOARDING_STEPS_TARGET.SIDEBAR_MORE_ACTION_ICON}
              type="button"
              disabled={isSubmitting}
              aria-label="Profile menu"
              className="tw:cursor-pointer tw:border-0 tw:bg-transparent tw:p-0 tw:outline-none"
            >
              {avatar}
            </button>
          }
          items={menuItems}
          align="start"
          side="right"
          open={isMenuOpen}
          onOpenChange={setIsMenuOpen}
        />
        {collapseToggle}
        {supportModals}
      </div>
    );
  }
  const handleOnboardingStepClick = (e) => {
    e.preventDefault();
    setIsMenuOpen(!isMenuOpen);
  };

  const profileTrigger = (
    <button
      id={ONBOARDING_STEPS_TARGET.SIDEBAR_MORE_ACTION_ICON}
      type="button"
      disabled={isSubmitting}
      className="tw:flex tw:items-center tw:gap-s tw:min-w-0 tw:cursor-pointer tw:border-0 tw:bg-transparent tw:text-left tw:outline-none"
      onClick={isOnboarding ? handleOnboardingStepClick : null}
    >
      {avatar}
      <Tooltip
        trigger={
          <div className="tw:flex tw:flex-col tw:min-w-0 tw:items-start">
            <Typography
              variant="h4"
              align="left"
              colorClass="tw:text-text-secondary"
              className="tw:max-w-30 tw:truncate"
            >
              {name}
            </Typography>
            {accountTypeLabel ? (
              <Typography
                variant="b3"
                align="left"
                colorClass="tw:text-text-secondary"
                className="tw:max-w-30 tw:truncate"
              >
                {accountTypeLabel}
              </Typography>
            ) : null}
          </div>
        }
        title={name}
        description={accountTypeLabel}
        side="bottom"
      />
      <Icon
        icon={isMenuOpen ? sidebarIcons.chevronDown : sidebarIcons.chevronRight}
        size={16}
        colorClass="tw:text-icon-primary"
        className="tw:shrink-0"
      />
    </button>
  );

  return (
    <>
      <div className="tw:flex tw:items-center tw:justify-between tw:w-full">
        <Dropdown
          trigger={profileTrigger}
          items={menuItems}
          align="start"
          side="bottom"
          triggerClassName="tw:min-w-0"
          open={isMenuOpen}
          onOpenChange={!isOnboarding ? setIsMenuOpen : null}
          modal={false}
        />
        {collapseToggle}
      </div>
      {supportModals}
    </>
  );
};

export { Profile };
